import React, { useState } from 'react';
import { Search, X, Mail, UserPlus, Check } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/form';
import { Badge } from '../../ui/badge';
import { WorkflowWizardData, WorkflowStepData } from '../workflow-wizard';
import { useDivisionUsers } from '../../../hooks/use-division-users';
import { UserDto } from '../../../types';

interface AssignApproversStepProps {
  divisionId: string;
  data: WorkflowWizardData;
  onChange: (updates: Partial<WorkflowWizardData>) => void;
}

export function AssignApproversStep({ divisionId, data, onChange }: AssignApproversStepProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [newEmail, setNewEmail] = useState('');

  // Fetch users from API with search filter
  const { data: usersResponse, isLoading: isLoadingUsers } = useDivisionUsers(
    divisionId,
    {
      search: searchQuery,
      active: true,
      limit: 50
    },
    searchQuery.length >= 2  // Only search when 2+ characters typed
  );

  const availableUsers = usersResponse?.users || [];
  const currentStep = data.steps[currentStepIndex];

  const updateCurrentStep = (updates: Partial<WorkflowStepData>) => {
    const updatedSteps = data.steps.map((step, idx) =>
      idx === currentStepIndex ? { ...step, ...updates } : step
    );
    onChange({ steps: updatedSteps });
  };

  const addUser = (userId: string) => {
    if (!currentStep.approverUserIds.includes(userId)) {
      updateCurrentStep({
        approverUserIds: [...currentStep.approverUserIds, userId]
      });
    }
  };

  const removeUser = (userId: string) => {
    updateCurrentStep({
      approverUserIds: currentStep.approverUserIds.filter(id => id !== userId)
    });
  };

  const addEmail = () => {
    const trimmedEmail = newEmail.trim();
    if (trimmedEmail && !currentStep.approverEmails.includes(trimmedEmail)) {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmedEmail)) {
        updateCurrentStep({
          approverEmails: [...currentStep.approverEmails, trimmedEmail]
        });
        setNewEmail('');
      } else {
        alert('Please enter a valid email address');
      }
    }
  };

  const removeEmail = (email: string) => {
    updateCurrentStep({
      approverEmails: currentStep.approverEmails.filter(e => e !== email)
    });
  };

  // No client-side filtering needed - API already handles search
  const selectedUsers = availableUsers.filter(user =>
    currentStep.approverUserIds.includes(user.id)
  );

  const hasApprovers = currentStep.approverUserIds.length > 0 || currentStep.approverEmails.length > 0;
  const bypassEnabled = currentStep.bypassIfNoApprover ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Assign Approvers
        </h3>
        <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">
          Assign existing users or invite new users to approve orders at each step
        </p>
      </div>

      {/* Step Navigation */}
      {data.steps.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {data.steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setCurrentStepIndex(index)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 ${
                currentStepIndex === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 hover:bg-gray-200 dark:hover:bg-secondary-600'
              }`}
            >
              <span className="font-medium">Step {step.step}</span>
              {(step.approverUserIds.length > 0 || step.approverEmails.length > 0) && (
                <Check className="w-4 h-4 text-green-400" />
              )}
            </button>
          ))}
        </div>
      )}

      {currentStep && (
        <div className="border border-gray-300 dark:border-secondary-600 rounded-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                {currentStep.name}
              </h4>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
                {currentStep.description || 'Configure approvers for this step'}
              </p>
            </div>
            <Badge variant={hasApprovers || bypassEnabled ? 'success' : 'warning'}>
              {currentStep.approverUserIds.length + currentStep.approverEmails.length} approver
              {currentStep.approverUserIds.length + currentStep.approverEmails.length !== 1 ? 's' : ''}
            </Badge>
          </div>

          {/* Search Existing Users */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Search Existing Users
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-secondary-500 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or username..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              />
            </div>
          </div>

          {/* Available Users List */}
          {searchQuery && (
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-secondary-700 rounded-lg">
              {isLoadingUsers ? (
                <div className="p-4 text-center text-gray-500 dark:text-secondary-400">
                  Loading users...
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-secondary-400">
                  No users found
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-secondary-700">
                  {availableUsers.map(user => {
                    const isSelected = currentStep.approverUserIds.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className="p-3 hover:bg-gray-50 dark:hover:bg-secondary-700 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-secondary-400">{user.email}</p>
                          {user.divisionRoles && user.divisionRoles.length > 0 && (
                            <Badge className="mt-1">
                              {user.divisionRoles[0].divisionName} - {user.divisionRoles[0].role}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant={isSelected ? 'outline' : 'primary'}
                          onClick={() => isSelected ? removeUser(user.id) : addUser(user.id)}
                        >
                          {isSelected ? 'Remove' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Selected Users ({selectedUsers.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <div
                    key={user.id}
                    className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                  >
                    <span>{user.firstName} {user.lastName}</span>
                    <button
                      onClick={() => removeUser(user.id)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite New Users by Email */}
          <div className="border-t border-gray-200 dark:border-secondary-700 pt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Invite New Users by Email
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-secondary-500 w-5 h-5" />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                  placeholder="Enter email address..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
              </div>
              <Button onClick={addEmail} disabled={!newEmail.trim()}>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-secondary-400">
              Invited users will receive an email to join the platform and be assigned to this approval step
            </p>
          </div>

          {/* Invited Emails */}
          {currentStep.approverEmails.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Invited Users ({currentStep.approverEmails.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {currentStep.approverEmails.map(email => (
                  <div
                    key={email}
                    className="inline-flex items-center gap-2 bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400 px-3 py-1.5 rounded-full text-sm"
                  >
                    <Mail className="w-3 h-3" />
                    <span>{email}</span>
                    <button
                      onClick={() => removeEmail(email)}
                      className="text-purple-500 hover:text-purple-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bypass Options */}
          <div className="border-t border-gray-200 dark:border-secondary-700 pt-4 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={currentStep.bypassIfNoApprover ?? false}
                onChange={(e) => updateCurrentStep({ bypassIfNoApprover: e.target.checked, auditBypass: e.target.checked ? currentStep.auditBypass : false })}
                className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                Bypass step if no approver is assigned
              </span>
            </label>
            {currentStep.bypassIfNoApprover && (
              <label className="flex items-center gap-2 cursor-pointer ml-6">
                <input
                  type="checkbox"
                  checked={currentStep.auditBypass ?? false}
                  onChange={(e) => updateCurrentStep({ auditBypass: e.target.checked })}
                  className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                  Record bypass in audit log
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {!hasApprovers && !bypassEnabled && (
        <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-yellow-900">
                No approvers assigned
              </h4>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                Please assign at least one approver to continue. You can search for existing users or invite new users by email.
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasApprovers && bypassEnabled && (
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-900">
                No approvers assigned — step will be bypassed
              </h4>
              <p className="mt-1 text-sm text-blue-700">
                This step has no approvers, but "Bypass step if no approver is assigned" is enabled, so orders will skip this step automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
